import React, { useState, useEffect } from 'react';
import { useChildren } from '../context/ChildProvider';
import { Spinner, Button } from '../components/UI';
import { Ikon } from '../helpers/icons';

const GlassAssessmentCard = ({ title, description, icon, onClick, disabled, glowColor }) => (
    <div 
        onClick={disabled ? null : onClick} 
        className={`
            relative overflow-hidden rounded-[1.5rem] p-5 cursor-pointer
            bg-white/50 backdrop-blur-xl border border-white/60
            shadow-[0_8px_30px_rgba(0,0,0,0.04)] hover:shadow-lg transition-all duration-500
            transform hover:-translate-y-1 active:scale-95
            ${disabled ? 'opacity-50 cursor-not-allowed grayscale' : `hover:border-${glowColor}-300`}
        `}
    >
        {!disabled && (
            <div className={`absolute -right-8 -top-8 w-32 h-32 bg-${glowColor}-200 rounded-full  blur-3xl opacity-40 transition-opacity duration-500 pointer-events-none`}></div>
        )}
        
        <div className="flex items-center space-x-4 relative z-10">
            <div className={`w-14 h-14 flex-shrink-0 rounded-[1.25rem] bg-gradient-to-br from-white to-white/50 flex items-center justify-center text-2xl shadow-sm border border-white/80`}>
                <div className={`text-${glowColor}-500 w-8 h-8 flex items-center justify-center`}>
                    {icon}
                </div>
            </div>
            <div>
                <h3 className="text-[15px] font-extrabold text-gray-800 tracking-tight leading-tight mb-1">{title}</h3>
                <p className="text-[11px] text-gray-500 font-medium leading-relaxed">{description}</p>
            </div>
        </div>
        
        {!disabled && (
            <div className="absolute top-1/2 right-4 transform -translate-y-1/2 opacity-0 hover:opacity-100 transition-all duration-300 translate-x-2 hover:translate-x-0">
                <div className={`w-8 h-8 bg-white/80 text-${glowColor}-500 rounded-full flex items-center justify-center shadow-sm border border-gray-100`}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" /></svg>
                </div>
            </div>
        )}
    </div>
);

export default function AssessmentHubScreen({ user, navigate, showToast }) {
    const { myChildren, sharedChildren, loading } = useChildren();
    const children = React.useMemo(() => [...myChildren, ...sharedChildren], [myChildren, sharedChildren]);
    const [selectedChildId, setSelectedChildId] = useState('');

    useEffect(() => {
        if (children.length > 0 && !selectedChildId) {
            setSelectedChildId(children[0].id);
        }
    }, [children, selectedChildId]);

    const createDemoChild = () => ({
        id: 'demo',
        name: 'Anak Demo',
        dob: { toDate: () => new Date(Date.now() - (36 * 30 * 24 * 60 * 60 * 1000)) },
        gender: 'Laki-laki'
    });

    const handleAssessmentClick = (type, isComprehensive = false) => {
        if (user.isAnonymous) {
            const demoChild = createDemoChild();
            const message = isComprehensive 
                ? "Mode Demo: Pemeriksaan lengkap untuk anak 36 bulan. Data tidak akan disimpan."
                : "Mode Demo: Data tidak akan disimpan secara permanen.";
            showToast(message, "info");
            
            if (isComprehensive) {
                navigate('comprehensiveAssessment', demoChild);
            } else {
                navigate('singleAssessment', demoChild, { type });
            }
            return;
        }

        const selectedChild = children.find(c => c.id === selectedChildId);
        if (isComprehensive) {
            navigate('comprehensiveAssessment', selectedChild);
        } else {
            navigate('singleAssessment', selectedChild, { type });
        }
    };

    if (loading && !user.isAnonymous) return <Spinner />;

    if (!user.isAnonymous && children.length === 0) {
        return (
            <div className="min-h-screen bg-[#F8FAFC] flex flex-col pt-20 px-5">
                <div className="text-center py-12 px-6 bg-white/50 backdrop-blur-xl rounded-[2.5rem] border border-white/60 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
                    <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center text-5xl mx-auto mb-6 shadow-sm">👶</div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2 tracking-tight">Belum Ada Data Anak</h3>
                    <p className="text-sm text-gray-500 mb-8 max-w-xs mx-auto leading-relaxed">
                        Silakan tambah data anak terlebih dahulu di halaman Beranda untuk memulai pemeriksaan.
                    </p>
                    <Button onClick={() => navigate('home')} className="bg-gray-900 text-white hover:bg-gray-800 shadow-xl w-full py-4 rounded-2xl">
                        🏠 Kembali ke Beranda
                    </Button>
                </div>
            </div>
        );
    }
    
    const selectedChild = !user.isAnonymous ? children.find(c => c.id === selectedChildId) : null;

    return (
        <div className="min-h-screen bg-[#F8FAFC] font-sans selection:bg-purple-200 pb-28 pt-10 px-5 relative">
            {/* Animated Background Mesh */}
            <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
                <div className="absolute top-[10%] left-[-20%] w-[60vw] h-[60vw] bg-sky-200 rounded-full  blur-3xl opacity-40 "></div>
                <div className="absolute top-[40%] right-[-20%] w-[50vw] h-[50vw] bg-indigo-200 rounded-full  blur-3xl opacity-40  "></div>
                <div className="absolute bottom-[-10%] left-[10%] w-[70vw] h-[70vw] bg-blue-200 rounded-full  blur-3xl opacity-30  "></div>
                <div className="absolute inset-0 bg-white/40 backdrop-blur-[50px]"></div>
            </div>

            <div className="relative z-10 max-w-md mx-auto space-y-6">
                <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-white/60 backdrop-blur-md rounded-[1.25rem] flex items-center justify-center text-3xl mx-auto mb-4 shadow-sm border border-white">
                        🔍
                    </div>
                    <h2 className="text-2xl font-extrabold text-gray-800 tracking-tight">Pusat Pemeriksaan</h2>
                    <p className="text-sm text-gray-500 mt-2">
                        {user.isAnonymous 
                            ? "Coba berbagai jenis pemeriksaan dengan data demo anak 36 bulan."
                            : "Pilih anak dan jenis pemeriksaan yang akan dilakukan."
                        }
                    </p>
                </div>

                {user.isAnonymous && (
                    <div className="bg-sky-100/80 backdrop-blur-md rounded-[1.5rem] p-5 border border-sky-200 shadow-sm">
                        <div className="flex items-center space-x-3 mb-2">
                            <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-xl shadow-sm">👤</div>
                            <div>
                                <h3 className="font-extrabold text-sky-800 text-sm">Mode Demo</h3>
                                <p className="text-[10px] text-sky-600 font-medium">Coba fitur dengan data contoh</p>
                            </div>
                        </div>
                        <p className="text-[11px] text-sky-700 leading-relaxed bg-white/50 p-3 rounded-xl">
                            <strong>Catatan:</strong> Anda sedang menggunakan mode demo. Hasil pemeriksaan tidak akan disimpan secara permanen.
                        </p>
                    </div>
                )}

                {!user.isAnonymous && (
                    <div className="bg-white/60 backdrop-blur-xl rounded-[1.5rem] p-5 border border-white shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
                        <label htmlFor="child-select" className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2">Pilih Anak</label>
                        <select 
                            id="child-select" 
                            value={selectedChildId} 
                            onChange={e => setSelectedChildId(e.target.value)} 
                            className="w-full bg-white/80 border border-gray-200 text-gray-800 text-sm font-bold rounded-xl focus:ring-sky-500 focus:border-sky-500 block p-3 shadow-sm outline-none"
                        >
                            {children.map(child => (
                                <option key={child.id} value={child.id}>
                                    {child.name} {(child.schoolName || child.className) ? `(${child.schoolName || 'PAUD Tidak Diketahui'}${child.className ? ' - Kelas ' + child.className : ''})` : ''}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                <div className="space-y-4">
                    <div className="mb-2">
                        <h3 className="text-[13px] font-bold text-gray-800 tracking-tight px-2 flex items-center space-x-2">
                            <span className="w-2 h-2 rounded-full bg-sky-500"></span>
                            <span>Paket Lengkap</span>
                        </h3>
                    </div>
                    <GlassAssessmentCard 
                        title="Pemeriksaan Komprehensif" 
                        description="7 Tahap sekaligus: Pertumbuhan, KPSP, KMME, GPPH, TDD, TDL, dan CHAT."
                        icon={<Ikon.Komprehensif />}
                        glowColor="sky"
                        onClick={() => handleAssessmentClick(null, true)}
                    />

                    <div className="pt-4 mt-6">
                        <h3 className="text-[13px] font-bold text-gray-800 tracking-tight px-2 mb-4 flex items-center space-x-2">
                            <span className="w-2 h-2 rounded-full bg-indigo-500"></span>
                            <span>Pemeriksaan Individual</span>
                        </h3>
                        <div className="grid gap-3">
                            <GlassAssessmentCard 
                                title="Deteksi Pertumbuhan" 
                                description="Ukur BB, TB, dan Lingkar Kepala."
                                icon={<Ikon.Pertumbuhan />} glowColor="indigo"
                                onClick={() => handleAssessmentClick('pertumbuhan')}
                            />
                            <GlassAssessmentCard 
                                title="Perkembangan (KPSP)" 
                                description="Pantau pencapaian perkembangan anak."
                                icon={<Ikon.KPSP />} glowColor="indigo"
                                onClick={() => handleAssessmentClick('kpsp')}
                            />
                            <GlassAssessmentCard 
                                title="Mental Emosional (KMME)" 
                                description="Skrining dini masalah emosional."
                                icon={<Ikon.KMME />} glowColor="purple"
                                onClick={() => handleAssessmentClick('mental')}
                            />
                            <GlassAssessmentCard 
                                title="Skrining GPPH" 
                                description="Pemusatan Perhatian dan Hiperaktivitas."
                                icon={<Ikon.GPPH />} glowColor="purple"
                                onClick={() => handleAssessmentClick('gpph')}
                            />
                            <GlassAssessmentCard 
                                title="Daya Dengar (TDD)" 
                                description="Pemeriksaan kemampuan pendengaran."
                                icon={<Ikon.TDD />} glowColor="blue"
                                onClick={() => handleAssessmentClick('tdd')}
                            />
                            <GlassAssessmentCard 
                                title="Daya Lihat (TDL)" 
                                description="Pemeriksaan kemampuan penglihatan."
                                icon={<Ikon.TDL />} glowColor="blue"
                                onClick={() => handleAssessmentClick('tdl')}
                            />
                            <GlassAssessmentCard 
                                title="Skrining Autisme (CHAT)" 
                                description="Deteksi dini risiko spektrum autisme."
                                icon={<Ikon.CHAT />} glowColor="fuchsia"
                                onClick={() => handleAssessmentClick('chat')}
                            />
                        </div>
                    </div>
                </div>

                {!user.isAnonymous && ['Tenaga Kesehatan', 'Guru PAUD'].includes(user.role) && (
                    <div className="pt-8">
                        <Button onClick={() => navigate('createIntervention', selectedChild)} className="bg-gray-900 text-white hover:bg-gray-800 shadow-xl w-full py-4 rounded-2xl">
                            📝 Buat Rencana Intervensi
                        </Button>
                    </div>
                )}

                {user.isAnonymous && (
                    <div className="bg-emerald-100/80 backdrop-blur-md rounded-[1.5rem] p-5 border border-emerald-200 mt-8">
                        <h3 className="font-extrabold text-emerald-800 text-sm mb-2">Ingin Menyimpan Data?</h3>
                        <p className="text-[11px] text-emerald-700 mb-4 leading-relaxed">
                            Buat akun untuk menyimpan data anak, riwayat pemeriksaan, dan mengakses semua fitur tanpa batasan.
                        </p>
                        <button 
                            onClick={() => showToast("Silakan logout dan pilih 'Buat Akun Baru' untuk mendaftar.", "info")}
                            className="w-full bg-emerald-600 text-white py-3 px-4 rounded-xl font-bold text-sm shadow-md hover:bg-emerald-700 transition-colors"
                        >
                            Informasi Pendaftaran
                        </button>
                    </div>
                )}
            </div>

            <style>{`
                
                    33% { transform: translate(30px, -50px) scale(1.1); }
                    66% { transform: translate(-20px, 20px) scale(0.9); }
                    100% { transform: translate(0px, 0px) scale(1); }
                }
                . { animation: blob 15s infinite alternate ease-in-out; }
                . { animation-delay: 2s; }
                . { animation-delay: 4s; }
            `}</style>
        </div>
    );
}
